import { useState } from 'react';
import { Button } from '@givebutter/primitives/button';
import * as Popover from '@givebutter/primitives/popover';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { maskWholeNumber, pluralize, unmaskNumber } from '@givebutter/util';
import { faCalendar, faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CreateTaskFormValues } from 'assets/js/pages/engage/workflows/actions/types';
import * as Form from 'components/form';
import { capitalize } from 'lodash';

interface CreateTaskDueAtFormValues {
    value: string;
    unit: string;
    skip_weekends: boolean;
}

interface CreateTaskDueAtFormFieldProps extends Popover.PopoverProps {}
const CreateTaskDueAtFormField = (props: CreateTaskDueAtFormFieldProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const { watch, setValue: setFormValue } = Form.useGivebutterFormContext<CreateTaskFormValues>();
    const due_at = watch('due_at');

    const methods = Form.useGivebutterForm<CreateTaskDueAtFormValues>({
        defaultValues: {
            value: maskWholeNumber(due_at?.value ?? '1'),
            unit: due_at?.unit ?? 'day',
            skip_weekends: due_at?.skip_weekends ?? true,
        },
    });
    const { control, watch: watchInternal, getValues } = methods;
    const [value, unit] = watchInternal(['value', 'unit']);

    return (
        <Popover.Root open={isOpen} onOpenChange={setIsOpen} {...props}>
            <Popover.Trigger asChild>
                {due_at ? (
                    <div className="flex cursor-pointer items-center rounded-lg border border-gray-200 py-2 pl-3 pr-1.5 hover:bg-gray-100">
                        <div className="flex items-center gap-1">
                            <FontAwesomeIcon
                                icon={faCalendar}
                                size="sm"
                                className="text-gray-500"
                            />

                            <span className="text-sm">
                                Due {maskWholeNumber(due_at.value)}{' '}
                                {pluralize(unmaskNumber(due_at.value), due_at.unit)} after
                            </span>
                            <Button
                                variant="none"
                                color="neutral"
                                className="px-1"
                                onClick={e => {
                                    e.preventDefault();
                                    setFormValue('due_at', null, { shouldDirty: true });
                                }}
                            >
                                <FontAwesomeIcon icon={faClose} size="sm" />
                            </Button>
                        </div>
                    </div>
                ) : (
                    <Button
                        variant="transparent"
                        shape="square"
                        className="hover:bg-gray-100"
                        aria-label="Configure due at field"
                    >
                        <FontAwesomeIcon icon={faCalendar} className="text-gray-500" />
                    </Button>
                )}
            </Popover.Trigger>

            <Popover.Portal>
                <Popover.Content
                    align="center"
                    side="bottom"
                    className="flex max-w-[500px] flex-col gap-4 p-4"
                >
                    <Form.Provider {...methods}>
                        <Form.Container>
                            <Form.Label name="due_at">Due date</Form.Label>
                            <div className="flex flex-row flex-wrap items-center gap-2">
                                <Form.Label className="sr-only" name="value">
                                    Due at value
                                </Form.Label>
                                <Form.TextInputRoot
                                    control={control}
                                    name="value"
                                    inputRootProps={{
                                        mask: 'whole-number',
                                        className: 'w-24',
                                    }}
                                >
                                    <Form.TextInputForm />
                                </Form.TextInputRoot>
                                <Form.Label className="sr-only" name="unit">
                                    Due at unit
                                </Form.Label>
                                <Form.SingleSelect control={control} name="unit">
                                    <SingleSelect.Trigger
                                        aria-label="Select due at unit"
                                        className="basis-min-content"
                                    >
                                        <SingleSelect.Value>
                                            {capitalize(pluralize(unmaskNumber(value), unit))} after
                                        </SingleSelect.Value>
                                    </SingleSelect.Trigger>
                                    <SingleSelect.Content>
                                        <SingleSelect.Item value="day">
                                            <SingleSelect.ItemText>Day</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="week">
                                            <SingleSelect.ItemText>Week</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="month">
                                            <SingleSelect.ItemText>Month</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                    </SingleSelect.Content>
                                </Form.SingleSelect>
                            </div>
                        </Form.Container>
                        <Form.Container>
                            <Form.Label
                                name="skip_weekends"
                                className="flex w-fit cursor-pointer items-center gap-x-2"
                            >
                                <Form.UseFormCheckboxInput control={control} name="skip_weekends" />
                                Skip weekends
                            </Form.Label>
                        </Form.Container>
                        <div className="ml-auto flex items-center gap-1">
                            <Button
                                variant="outline"
                                color="neutral"
                                size="sm"
                                onClick={() => setIsOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="solid"
                                size="sm"
                                onClick={() => {
                                    setFormValue('due_at', getValues(), { shouldDirty: true });
                                    setIsOpen(false);
                                }}
                            >
                                Apply
                            </Button>
                        </div>
                    </Form.Provider>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};

export { CreateTaskDueAtFormField };
